/**
 * src/components/Financial/modules/PlanetAngleReturnModule.jsx
 * 【完全版：基準点リスト復元・常時設定表示・未来対応・描画位置調整】
 */
import React, { useMemo } from "react";
import { RefreshCw, Info } from "lucide-react";
import * as Astro from "../../../astronomy.js";
import { PLANET_SYMBOLS, PLANET_COLORS } from "../../../utils/constants.js";

/**
 * 惑星位置計算の共通化 (黄経・赤緯の両方を取得するように修正)
 * 存在しない関数へのアクセスを防止し、クラッシュを避けるロジックを追加
 */
export const getPlanetPositions = function (
  date,
  isHelio,
  isSidereal,
  lat,
  lon,
) {
  if (!date) return null;

  // 1. 黄経 (Longitude) の取得
  var p = {};
  try {
    p = isHelio
      ? typeof Astro.getAllHeliocentricLongitudes === "function"
        ? Astro.getAllHeliocentricLongitudes(date)
        : {}
      : typeof Astro.getAllPlanetLongitudesExtended === "function"
        ? Astro.getAllPlanetLongitudesExtended(
          date,
          lat || 35.6585,
          lon || 139.7486,
        )
        : {};
  } catch (e) {
    p = {};
  }

  // 惑星リスト (フォールバック用)
  var bodies = isHelio
    ? [
      "Mercury",
      "Venus",
      "Earth",
      "Mars",
      "Jupiter",
      "Saturn",
      "Uranus",
      "Neptune",
      "Pluto",
    ]
    : [
      "Sun",
      "Moon",
      "Mercury",
      "Venus",
      "Mars",
      "Jupiter",
      "Saturn",
      "Uranus",
      "Neptune",
      "Pluto",
    ];

  // 2. 赤緯 (Declination) または 黄緯 (Latitude) の取得
  if (!isHelio) {
    // ジオセントリックの場合
    var decs = null;
    // 複数の可能性のある関数名をチェックして取得を試みる
    if (typeof Astro.getAllPlanetDeclinations === "function") {
      decs = Astro.getAllPlanetDeclinations(date);
    } else if (typeof Astro.getAllDeclinations === "function") {
      decs = Astro.getAllDeclinations(date);
    } else if (
      typeof Astro.getPlanetDeclination === "function" ||
      typeof Astro.getDeclination === "function"
    ) {
      var getDecFunc = Astro.getPlanetDeclination || Astro.getDeclination;
      decs = {};
      bodies.forEach(function (b) {
        try {
          decs[b] = getDecFunc(b, date);
        } catch (err) { }
      });
    }

    if (decs) {
      for (var key in decs) {
        p[key + "_dec"] = decs[key];
      }
    }

    // 拡張感受点 (ASC/MC等) の取得
    var ext = null;
    if (typeof getExtendedPoints === "function") {
      ext = getExtendedPoints(date);
    } else if (typeof Astro.getExtendedPoints === "function") {
      ext = Astro.getExtendedPoints(date);
    }

    if (ext) {
      p = Object.assign({}, p, ext);
    }
  } else {
    // ヘリオセントリックの場合：赤緯の代わりに「日心黄緯 (Latitude)」を取得
    var helioLats = null;
    if (typeof Astro.getAllHeliocentricLatitudes === "function") {
      helioLats = Astro.getAllHeliocentricLatitudes(date);
    } else if (typeof Astro.getHeliocentricLatitude === "function") {
      helioLats = {};
      bodies.forEach(function (b) {
        try {
          helioLats[b] = Astro.getHeliocentricLatitude(b, date);
        } catch (err) { }
      });
    }

    if (helioLats) {
      for (var hKey in helioLats) {
        p[hKey + "_dec"] = helioLats[hKey];
      }
    }
  }

  // 3. サイデリアル補正 (黄経のみに適用)
  if (isSidereal) {
    // 修正：固定値ではなく、その日付時点での精密なアヤナンシャを算出する
    var ayan = 0;
    if (typeof Astro.calculateLahiriAyanamsha === "function") {
      // 統一された計算関数がある場合
      ayan = Astro.calculateLahiriAyanamsha(date);
    } else if (typeof Astro.dateToJulianDay === "function") {
      // 存在しない場合は Julian Day から直接算出 (Lahiri公式)
      var jd = Astro.dateToJulianDay(date);
      ayan = 23.85 + ((50.2564 / 3600) * (jd - 2451545.0)) / 365.25;
    } else {
      // フォールバック
      ayan = 23.8528;
    }

    for (var k in p) {
      // 黄経（Longitude）に対してのみ補正を適用。赤緯（_dec）は物理的な角度のため除外。
      if (typeof p[k] === "number" && k.indexOf("_dec") === -1) {
        p[k] = (p[k] - ayan + 360) % 360;
      }
    }
  }
  return p;
};

/**
 * 惑星角度リターン計算（高速化・未来対応版）
 */
export const calculateAngleReturns = function (
  timeline,
  baseDate,
  config,
  isHelio,
  isSidereal,
  lat,
  lon,
) {
  if (!baseDate || !timeline || timeline.length === 0) return [];
  var hits = [];
  var planets = Array.from(config.selectedPlanets || []);
  var offsets = config.selectedAngles || [0, 90, 180, 270];
  var coordinate = config.coordinate || "longitude";
  var seen = {};

  var getDate = function (row) {
    return row.timestamp ? new Date(row.timestamp) : new Date(row.Date);
  };

  // 全タイムラインの天体位置を一度だけ計算してキャッシュ
  var timelinePositions = timeline.map(function (row) {
    return getPlanetPositions(getDate(row), isHelio, isSidereal, lat, lon);
  });

  // 基準日の位置を取得
  var basePos = getPlanetPositions(
    new Date(baseDate),
    isHelio,
    isSidereal,
    lat,
    lon,
  );
  if (!basePos) return [];

  /**
   * 修正ポイント：
   * 黄経座標において、最短距離が90度以上離れている場合の符号反転（＝対面地点でのジャンプ）を無視するように変更。
   * これにより、0度リターンの判定が180度地点で反応する不具合が解消されます。
   */
  var crossed = function (prev, curr, target, coord) {
    if (coord === "longitude") {
      // target（目標黄経）への最短符号付き距離を算出
      var d1 = ((prev - target + 540) % 360) - 180;
      var d2 = ((curr - target + 540) % 360) - 180;

      // 1. 符号が反転していること
      // 2. かつ、その地点が target の近傍（90度以内）であること
      // (対面地点 180度付近では abs(d) が 180 に近くなるため、これを除外します)
      return (
        Math.abs(d1) < 90 &&
        Math.abs(d2) < 90 &&
        ((d1 <= 0 && d2 > 0) || (d1 >= 0 && d2 < 0))
      );
    } else {
      // 赤緯などは円環ではないため、単純な境界チェックを維持
      return (
        (prev <= target && curr > target) || (prev >= target && curr < target)
      );
    }
  };

  planets.forEach(function (planet) {
    var baseVal =
      coordinate === "longitude" ? basePos[planet] : basePos[planet + "_dec"];
    if (baseVal === null || baseVal === undefined) return;

    var targets = offsets.map(function (off) {
      return coordinate === "longitude"
        ? (baseVal + off + 360) % 360
        : baseVal + off;
    });

    for (var i = 1; i < timelinePositions.length; i++) {
      var p1 = timelinePositions[i - 1];
      var p2 = timelinePositions[i];

      targets.forEach(function (targetAngle, idx) {
        var v1 = coordinate === "longitude" ? p1[planet] : p1[planet + "_dec"];
        var v2 = coordinate === "longitude" ? p2[planet] : p2[planet + "_dec"];

        if (
          v1 !== undefined &&
          v2 !== undefined &&
          crossed(v1, v2, targetAngle, coordinate)
        ) {
          var key = planet + "_" + offsets[idx] + "_" + i;
          if (!seen[key]) {
            seen[key] = true;
            hits.push({
              index: i,
              planet: planet,
              angle: offsets[idx],
              color: PLANET_COLORS[planet] || "#2c2d96",
            });
          }
        }
      });
    }
  });
  return hits;
};

/**
 * ピボット（主要高値・安値）を自動判定する内部関数
 */
const findPivots = function (timeline, period) {
  var pivots = new Set();
  if (!timeline || timeline.length === 0) return pivots;
  var p = period || 5;

  for (var i = p; i < timeline.length - p; i++) {
    var current = timeline[i];
    var isHigh = true;
    var isLow = true;

    for (var j = i - p; j <= i + p; j++) {
      if (i === j) continue;
      var other = timeline[j];

      // 高値判定 (近傍 p バーの中で最高)
      var currH = current.High !== undefined ? current.High : current.price;
      var otherH = other.High !== undefined ? other.High : other.price;
      if (otherH >= currH) isHigh = false;

      // 安値判定 (近傍 p バーの中で最低)
      var currL = current.Low !== undefined ? current.Low : current.price;
      var otherL = other.Low !== undefined ? other.Low : other.price;
      if (otherL <= currL) isLow = false;
    }

    if (isHigh || isLow) {
      pivots.add(i);
    }
  }
  return pivots;
};

/**
 * 惑星角度リターン 描画レイヤー (ピボット重複マーカー機能付き)
 */
export const PlanetAngleReturnLayer = function (props) {
  var timeline = props.timeline || [];
  var angleReturnHits = props.angleReturnHits || [];
  var baseIndex = props.baseIndex;
  var displayRange = props.displayRange;
  var coords = props.coords;
  var planetColors = props.planetColors || {};
  var isDarkMode = props.isDarkMode;

  // 1. 自動ピボット判定 (5バー期間)
  var pivots = useMemo(
    function () {
      return findPivots(timeline, 5);
    },
    [timeline],
  );

  var processedHits = (function () {
    var counts = {};
    return angleReturnHits.map(function (hit) {
      var idx = hit.index;
      var isBaseDate = idx === baseIndex;
      var pColor = planetColors[hit.planet] || hit.color;
      var level = 0;
      if (!isBaseDate) {
        level = counts[idx] || 0;
        counts[idx] = level + 1;
      }
      return Object.assign({}, hit, {
        level: level,
        isBaseDate: isBaseDate,
        pColor: pColor,
        // ピボットと重なっているか判定
        isPivotOverlap: pivots.has(idx),
      });
    });
  })();

  return (
    <g>
      {processedHits.map(function (hit, i) {
        if (
          hit.index < displayRange.start ||
          hit.index >= displayRange.start + displayRange.count
        )
          return null;

        var xPos = coords.getX(hit.index - displayRange.start);
        var yPos = coords.pad.t - 30 - hit.level * 25;

        // 当該日の高値を取得してマーカーのY座標を決定
        var node = timeline[hit.index];
        var highVal = node.High !== undefined ? node.High : node.price;
        var markerY = coords.getY(highVal) - 15;

        return (
          <g key={"angle-ret-" + i}>
            {/* 垂直グリッド線 */}
            <line
              x1={xPos}
              y1={coords.pad.t}
              x2={xPos}
              y2={coords.pad.t + coords.iH}
              stroke={hit.pColor}
              strokeWidth="1"
              strokeDasharray="2, 2"
              opacity="0.6"
            />

            {/* ピボット重複マーカー (●) */}
            {hit.isPivotOverlap && (
              <circle
                cx={xPos}
                cy={markerY}
                r="4"
                fill={hit.pColor}
                style={{
                  paintOrder: "stroke",
                  stroke: isDarkMode ? "#0b0e14" : "#ffffff",
                  strokeWidth: "2px",
                }}
              />
            )}

            {!hit.isBaseDate && (
              <g transform={"translate(" + xPos + ", " + yPos + ")"}>
                <text
                  fontSize="16"
                  fill={hit.pColor}
                  textAnchor="middle"
                  fontWeight="bold"
                  style={{
                    paintOrder: "stroke",
                    stroke: isDarkMode ? "#0b0e14" : "#ffffff",
                    strokeWidth: "3px",
                    strokeLinejoin: "round",
                  }}
                >
                  {PLANET_SYMBOLS[hit.planet]}
                </text>
                <text
                  y="10"
                  fontSize="8"
                  fontWeight="bold"
                  fill={hit.pColor}
                  textAnchor="middle"
                >
                  {hit.angle}°
                </text>
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
};

/**
 * 基準点（Base Date）の天体位置リスト描画 (修正版：ReferenceError解消)
 */
export const BaseDatePlanetList = function (props) {
  var basePos = props.basePos;
  var coordinate = props.coordinate;
  var planetColors = props.planetColors || {};
  var isDarkMode = props.isDarkMode; // ★ 修正：propsから取得

  if (!basePos) return null;

  var planets = [
    "Sun",
    "Moon",
    "Mercury",
    "Venus",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];

  return (
    <div
      style={{
        position: "absolute",
        bottom: "85px",
        left: "20px",
        backgroundColor: isDarkMode
          ? "rgba(28, 31, 38, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
        color: isDarkMode ? "#ffffff" : "#1e293b",
        padding: "12px",
        borderRadius: "10px",
        fontSize: "11px",
        zIndex: 105,
        border: "1px solid " + (isDarkMode ? "#2d313d" : "#e2e8f0"),
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        minWidth: "130px",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          fontSize: "10px",
          color: isDarkMode ? "#6366f1" : "#2c2d96",
          marginBottom: "4px",
          borderBottom: "1.5px solid " + (isDarkMode ? "#6366f1" : "#2c2d96"),
          paddingBottom: "2px",
        }}
      >
        基準点座標: {coordinate === "longitude" ? "黄経" : "赤緯"}
      </div>
      {planets.map(function (p) {
        var val = coordinate === "longitude" ? basePos[p] : basePos[p + "_dec"];
        if (val === undefined || val === null) return null;
        var displayVal =
          coordinate === "longitude"
            ? Math.floor(val) +
            "°" +
            Math.floor((val % 1) * 60)
              .toString()
              .padStart(2, "0") +
            "'"
            : val.toFixed(2) + "°";
        return (
          <div
            key={p}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  color: planetColors[p] || (isDarkMode ? "#fff" : "#333"),
                  fontSize: "15px",
                }}
              >
                {PLANET_SYMBOLS[p]}
              </span>
              <span
                style={{
                  color: isDarkMode ? "#9ca3af" : "#64748b",
                  fontSize: "9px",
                }}
              >
                {p}
              </span>
            </div>
            <span
              style={{
                fontWeight: "bold",
                color: isDarkMode ? "#fff" : "#1e293b",
                fontFamily: "monospace",
              }}
            >
              {displayVal}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * 惑星角度リターン 設定UI (修正版：TypeErrorを解消し、ダークモードに対応)
 */
export const AngleReturnSettingsUI = function (props) {
  var config = props.config;
  var setConfig = props.setConfig;
  var showAngleReturns = props.showAngleReturns;
  var setShowAngleReturns = props.setShowAngleReturns;
  var isDarkMode = props.isDarkMode;

  // ★ 修正：propsから直接受け取るように変更し、undefined ガードを追加
  var selectedPlanets = props.selectedPlanets || new Set(["Sun"]);
  var setSelectedPlanets = props.setSelectedPlanets || function () { };

  var isHeliocentric = props.isHeliocentric === true || props.isHeliocentric === "true";

  var planets = isHeliocentric
    ? [
      "Mercury",
      "Venus",
      "Earth",
      "Mars",
      "Jupiter",
      "Saturn",
      "Uranus",
      "Neptune",
      "Pluto",
    ]
    : [
      "Sun",
      "Moon",
      "Mercury",
      "Venus",
      "Mars",
      "Jupiter",
      "Saturn",
      "Uranus",
      "Neptune",
      "Pluto",
    ];

  var btnSt = function (active) {
    return {
      padding: "6px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      color: active ? "#fff" : isDarkMode ? "#9ca3af" : "#64748b",
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "#374151" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  // ★ 修正：props.setSelectedPlanets を使用するように変更
  var togglePlanet = function (p) {
    var next = new Set(selectedPlanets);
    if (next.has(p)) {
      next.delete(p);
    } else {
      next.add(p);
    }
    setSelectedPlanets(next);
  };

  var toggleAngle = function (a) {
    var currentAngles = config.selectedAngles || [];
    var next;
    if (currentAngles.indexOf(a) !== -1) {
      next = currentAngles.filter(function (x) {
        return x !== a;
      });
    } else {
      next = currentAngles.concat([a]);
    }
    setConfig(Object.assign({}, config, { selectedAngles: next }));
  };

  return (
    <div
      style={{
        background: isDarkMode ? "#1c1f26" : "#f8fafc",
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + (isDarkMode ? "#2d313d" : "#e2e8f0"),
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: isDarkMode ? "#fff" : "#1e293b",
          }}
        >
          <RefreshCw size={18} /> <span>惑星角度リターン (Angle Returns)</span>
        </div>
        <button
          onClick={function () {
            setShowAngleReturns(!showAngleReturns);
          }}
          style={btnSt(showAngleReturns)}
        >
          {showAngleReturns ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          marginBottom: "10px",
          color: isDarkMode ? "#9ca3af" : "#64748b",
        }}
      >
        基準日設定 : 対象日(主要高値・安値)のローソク足をダブルクリック。
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          opacity: showAngleReturns ? 1 : 0.5,
          pointerEvents: showAngleReturns ? "auto" : "none",
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "bold",
              color: isDarkMode ? "#9ca3af" : "#64748b",
            }}
          >
            座標系:
          </span>
          {["longitude", "declination"].map(function (c) {
            return (
              <label
                key={c}
                style={{
                  fontSize: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: isDarkMode ? "#fff" : "#1e293b",
                }}
              >
                <input
                  type="radio"
                  checked={config.coordinate === c}
                  onChange={function () {
                    setConfig(Object.assign({}, config, { coordinate: c }));
                  }}
                />
                {c === "longitude" ? "黄経" : (isHeliocentric ? "日心黄緯" : "赤緯")}
              </label>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "bold",
              color: isDarkMode ? "#9ca3af" : "#64748b",
              marginTop: "8px",
            }}
          >
            角度:
          </span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {/* 表示する角度のリスト */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map(function (a) {
              var currentSelected = config.selectedAngles || [];
              // indexOf を使用して選択状態を判定
              var isSelected = currentSelected.indexOf(a) !== -1;
              return (
                <button
                  key={a}
                  onClick={function () {
                    toggleAngle(a);
                  }}
                  style={{
                    padding: "4px 10px",
                    fontSize: "11px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    background: isSelected
                      ? "#2c2d96"
                      : isDarkMode
                        ? "#2d2c33"
                        : "#fff",
                    color: isSelected
                      ? "#fff"
                      : isDarkMode
                        ? "#9ca3af"
                        : "#64748b",
                    border:
                      "1px solid " +
                      (isSelected
                        ? "#2c2d96"
                        : isDarkMode
                          ? "#374151"
                          : "#cbd5e1"),
                  }}
                >
                  {a}°
                </button>
              );
            })}
          </div>
        </div>

        {/* ★ 修正：isSelected の判定に props.selectedPlanets を使用 */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {planets.map(function (p) {
            var isSelected =
              selectedPlanets && selectedPlanets.has
                ? selectedPlanets.has(p)
                : false;
            return (
              <button
                key={p}
                onClick={function () {
                  togglePlanet(p);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 14px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                  background: isSelected
                    ? isDarkMode
                      ? "#2c2d96"
                      : "#fff"
                    : isDarkMode
                      ? "#1c1f26"
                      : "#f1f5f9",
                  border:
                    "1.5px solid " +
                    (isSelected
                      ? PLANET_COLORS[p] || "#2c2d96"
                      : isDarkMode
                        ? "#2d313d"
                        : "#e2e8f0"),
                  color: isSelected
                    ? isDarkMode
                      ? "#fff"
                      : PLANET_COLORS[p] || "#2c2d96"
                    : isDarkMode
                      ? "#9ca3af"
                      : "#64748b",
                }}
              >
                {PLANET_SYMBOLS[p]} {p}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          color: isDarkMode ? "#9ca3af" : "#64748b",
          fontSize: "10px",
          marginTop: "15px",
          padding: "0 4px",
        }}
      >
        <Info size={14} />
        <span>
          特定の日時を起点に、天体が特定の角度（0度、90度等）を移動したタイミングを特定します。
        </span>
      </div>
    </div>
  );
};
